import { UtcInstant, type RoundTiming } from '@urbanrats/contracts';
import { err, ok, type Result } from './result.ts';

/** Server-authoritative wall clock, injected everywhere (never Date.now() in domain logic). */
export interface TimeSource {
  nowMs(): number;
}

export const systemTimeSource: TimeSource = { nowMs: () => Date.now() };

export class FakeClock implements TimeSource {
  private current: number;
  constructor(startMs: number) {
    this.current = startMs;
  }
  nowMs(): number {
    return this.current;
  }
  advance(ms: number): void {
    if (ms < 0) throw new RangeError('FakeClock cannot go backwards via advance');
    this.current += ms;
  }
  set(ms: number): void {
    this.current = ms;
  }
}

export function toUtcInstant(ms: number): UtcInstant {
  return UtcInstant.parse(new Date(ms).toISOString());
}

export function fromUtcInstant(i: UtcInstant): number {
  return Date.parse(i);
}

export type TimingError =
  | { code: 'TIMING_ORDER'; reason: string }
  | { code: 'GUARD_VIOLATION'; reason: string }
  | { code: 'TIMING_UNTRUSTED'; reason: string };

/**
 * Enforces closeAt + guard < captureStartAt - e (docs §7).
 * If the capture clock error bound is unknown the round can only run without real money.
 */
export function validateRoundTiming(t: RoundTiming, opts: { realMoney: boolean }): Result<RoundTiming, TimingError> {
  const open = fromUtcInstant(t.bettingOpenAt);
  const close = fromUtcInstant(t.bettingCloseAt);
  const start = fromUtcInstant(t.captureStartAt);
  const end = fromUtcInstant(t.captureEndAt);
  const verify = fromUtcInstant(t.verifyDeadlineAt);
  if (!(open < close)) return err({ code: 'TIMING_ORDER', reason: 'bettingOpenAt must be before bettingCloseAt' });
  if (!(start < end)) return err({ code: 'TIMING_ORDER', reason: 'captureStartAt must be before captureEndAt' });
  if (!(end <= verify)) return err({ code: 'TIMING_ORDER', reason: 'verifyDeadlineAt must not precede captureEndAt' });
  if (t.clockErrorBoundMs === null && opts.realMoney)
    return err({ code: 'TIMING_UNTRUSTED', reason: 'capture clock error bound unknown; real money not allowed' });
  const e = t.clockErrorBoundMs ?? 0;
  if (!(close + t.guardMs < start - e))
    return err({ code: 'GUARD_VIOLATION', reason: `closeAt + guard (${close + t.guardMs}) must be < captureStartAt - e (${start - e})` });
  return ok(t);
}

/** Server-side acceptance window check. The browser countdown never decides acceptance. */
export function isBettingWindowOpen(nowMs: number, t: RoundTiming): boolean {
  return nowMs >= fromUtcInstant(t.bettingOpenAt) && nowMs < fromUtcInstant(t.bettingCloseAt);
}
